import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import { format } from 'date-fns';
import { Clock, ArrowRight, Newspaper } from 'lucide-react';
import { useMemo } from 'react';

interface NewsArticle {
  id: string;
  title: string;
  description: string | null;
  url: string;
  image_url: string | null;
  source: string | null;
  published_at: string | null;
  is_featured: boolean;
}

/** Splits a description into bullet-style summary points */
function getSummaryPoints(description: string | null): string[] {
  if (!description) return [];
  // Split on sentence boundaries, take up to 3 points
  const sentences = description
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 10);
  return sentences.slice(0, 3);
}

export function NewsBeepsCarousel() {
  const { data: articles, isLoading } = useQuery({
    queryKey: ['homepage-news'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('id, title, description, url, image_url, source, published_at, is_featured')
        .order('published_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      return data as NewsArticle[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const sortedArticles = useMemo(() => {
    if (!articles) return [];
    return [...articles].sort((a, b) => {
      if (a.is_featured !== b.is_featured) return b.is_featured ? 1 : -1;
      return new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime();
    });
  }, [articles]);

  if (isLoading) {
    return (
      <section className="py-16 bg-muted/30">
        <div className="container">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} className="h-96 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!sortedArticles.length) return null;

  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold">Real Estate News</h2>
              <p className="text-sm text-muted-foreground">Swipe to browse latest updates</p>
            </div>
          </div>
          <Link to="/news">
            <Button variant="outline" size="sm" className="gap-2">
              All News <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        <Carousel
          opts={{
            align: 'start',
            loop: true,
            skipSnaps: false,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {sortedArticles.map((article) => {
              const points = getSummaryPoints(article.description);

              return (
                <CarouselItem
                  key={article.id}
                  className="pl-4 basis-[85%] sm:basis-[60%] md:basis-[40%] lg:basis-[33.333%]"
                >
                  <Card className="h-full overflow-hidden flex flex-col border-0 shadow-md hover:shadow-lg transition-shadow">
                    {/* Image */}
                    {article.image_url ? (
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={article.title}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] bg-muted flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-muted-foreground/30" />
                      </div>
                    )}

                    {/* Content */}
                    <CardContent className="p-5 flex-1 flex flex-col">
                      {/* Source & Date */}
                      <div className="flex items-center gap-2 mb-3">
                        {article.source && (
                          <Badge variant="secondary" className="text-xs font-medium">
                            {article.source}
                          </Badge>
                        )}
                        {article.published_at && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(article.published_at), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="font-semibold text-base leading-snug line-clamp-2 mb-3">
                        {article.title}
                      </h3>

                      {/* Summary Bullets */}
                      {points.length > 0 && (
                        <ul className="space-y-2 mb-4 flex-1">
                          {points.map((point, idx) => (
                            <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                              <span className="line-clamp-2">{point}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {!points.length && article.description && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
                          {article.description}
                        </p>
                      )}

                      {/* Read More */}
                      <Link to={`/news/${article.id}`} className="mt-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2 rounded-full"
                        >
                          Read more
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-4" />
          <CarouselNext className="hidden md:flex -right-4" />
        </Carousel>
      </div>
    </section>
  );
}
